import type {
  TMinorActionPromptDataDealBreaker,
  TPortfolioNeighborhoodId,
} from '@kapitalist/contract/types'
import {skins} from '~/game/skins'
import Card from '~/models/game/Card'
import type GamePlayer from '~/models/game/GamePlayer'
import {PlayerGamePosition} from '~/models/game/GamePosition'
import type {
  TMinorActionPromptUserFacingDescriptionData,
  TMinorActionPromptValidResponseData,
} from '~/models/game/MinorActionPrompt'
import MinorActionPrompt from '~/models/game/MinorActionPrompt'
import MinorActionAcceptDealBreaker from '~/models/game/MinorActions/MinorActionAcceptDealBreaker'
import type {AnyMinorAction} from '~/models/game/types'
import {mutateIf} from '~/utils/etc'
import {sentenceCase, xVerbY} from '~/utils/language'

export default class MinorActionPromptDealBreaker extends MinorActionPrompt<TMinorActionPromptDataDealBreaker> {
  portfolioNeighborhoodId: TPortfolioNeighborhoodId // the dealbroken portfolio

  constructor(
    data: TMinorActionPromptDataDealBreaker,
    majorActor: GamePlayer,
    minorActor: GamePlayer
  ) {
    super(data, majorActor, minorActor)
    this.portfolioNeighborhoodId = data.portfolioNeighborhoodId
  }

  protected _validResponses(
    data: TMinorActionPromptValidResponseData
  ): AnyMinorAction | AnyMinorAction[] {
    return this.withJustSayNo(
      data,
      new MinorActionAcceptDealBreaker({
        minorActorPlayerId: this.minorActorPlayerId,
        portfolioNeighborhoodId: this.portfolioNeighborhoodId,
        type: 'dealBreaker',
      })
    )
  }

  public userFacingDescription(
    data: TMinorActionPromptUserFacingDescriptionData
  ): string {
    const {gamePosition, tense, skin} = data
    const currentPlayer =
      gamePosition instanceof PlayerGamePosition
        ? gamePosition.currentPlayer
        : undefined
    const {majorActor, minorActor} = this

    const {conjugatedVerb, xNoun, yNounPossessive} = xVerbY(
      tense,
      minorActor,
      'respondTo',
      majorActor,
      currentPlayer?.id
    )

    const dropFirstNoun = tense === 'imperativePrompt'
    return skins[skin].gameLogDescriptions.minorActionPrompts.dealBreaker({
      tense,
      actor: dropFirstNoun
        ? undefined
        : xNoun === minorActor.username
          ? xNoun
          : sentenceCase(xNoun),
      verb: mutateIf(conjugatedVerb, dropFirstNoun, sentenceCase),
      targetPossessive: yNounPossessive,
      cardName: Card.displayNameForCard('dealBreaker', skin),
    })
  }
}
